import React, { useState } from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';

/** Feature flag: set REACT_APP_ENABLE_OAUTH=true to show Google/Apple sign-in buttons. */
const OAUTH_ENABLED = (process.env.REACT_APP_ENABLE_OAUTH ?? '').toLowerCase() === 'true';

interface OAuthButtonsProps {
  mode?: 'login' | 'signup';
  onError?: (error: string) => void;
}

const OAuthButtons: React.FC<OAuthButtonsProps> = ({ mode = 'login', onError }) => {
  const { loginWithGoogle, loginWithApple } = useAuth();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [error, setError] = useState('');

  if (!OAUTH_ENABLED) {
    return null;
  }

  const handleGoogleLogin = async () => {
    setError('');
    setIsGoogleLoading(true);
    
    try {
      await loginWithGoogle();
      // User will be redirected to Google, so no need to do anything else
    } catch (err: any) {
      console.error('Google OAuth error:', err);
      const errorMessage = err.message || 'Error al iniciar sesión con Google';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
      setIsGoogleLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setError('');
    setIsAppleLoading(true);
    
    try {
      await loginWithApple();
      // User will be redirected to Apple, so no need to do anything else
    } catch (err: any) {
      console.error('Apple OAuth error:', err);
      const errorMessage = err.message || 'Error al iniciar sesión con Apple';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
      setIsAppleLoading(false);
    }
  };

  const actionText = mode === 'login' ? 'Continuar' : 'Registrarse';

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Google Button */}
      <Button
        fullWidth
        variant="outlined"
        onClick={handleGoogleLogin}
        disabled={isGoogleLoading || isAppleLoading}
        sx={{
          mb: 1.5,
          py: 1.2,
          textTransform: 'none',
          fontSize: '0.95rem',
          fontWeight: 500,
          color: '#3c4043',
          backgroundColor: '#ffffff',
          borderColor: '#dadce0',
          '&:hover': {
            backgroundColor: '#f8f9fa',
            borderColor: '#dadce0',
          },
          '&:disabled': {
            backgroundColor: '#f8f9fa',
            borderColor: '#dadce0',
          },
        }}
      >
        <Box
          component="img"
          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
          alt="Google"
          sx={{
            width: 18,
            height: 18,
            mr: 1.5,
          }}
        />
        {isGoogleLoading ? 'Redirigiendo...' : `${actionText} con Google`}
      </Button>

      {/* Apple Button */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleAppleLogin}
        disabled={isGoogleLoading || isAppleLoading}
        sx={{
          mb: 2,
          py: 1.2,
          textTransform: 'none',
          fontSize: '0.95rem',
          fontWeight: 500,
          color: '#ffffff',
          backgroundColor: '#000000',
          '&:hover': {
            backgroundColor: '#333333',
          },
          '&:disabled': {
            backgroundColor: '#666666',
          },
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 24 24"
          sx={{
            width: 18,
            height: 18,
            mr: 1.5,
            fill: 'currentColor',
          }}
        >
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </Box>
        {isAppleLoading ? 'Redirigiendo...' : `${actionText} con Apple`}
      </Button>

      {/* Divider */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          my: 3,
        }}
      >
        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
        <Typography
          variant="body2"
          sx={{
            px: 2,
            color: '#666666',
            fontSize: '0.875rem',
          }}
        >
          O continúa con email
        </Typography>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
      </Box>
    </Box>
  );
};

export default OAuthButtons;
